import React, { useState } from 'react';
import { observer } from 'mobx-react';

import MainContactInfo from '../customers/components/MainContactInfo';
import SelectGroup from 'components/groups/SelectGroup';
import { IField } from 'components/shared/form/IField';
import Checkbox from 'shared/Checkbox/components/Checkbox';
import SubmitRow from 'customers/components/SubmitRow';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import { CustomerLead } from './model/CustomerLead';
import { deserialise } from 'kitsu-core';
import CustomerLeadType from 'types/model/customerLead';
import { JsonApi } from 'types/utils/jsonApi';
import Routes from 'routes';
import Icon from 'components/elements/Icon';
import useField from 'customers/hooks/useField';
import LeadDetails, { CountryOption } from './components/LeadDetails';
import { ReasonGet } from 'types/model/reason';
export interface LeadFormProps {
  lead: JsonApi<CustomerLeadType>;
  fields: IField[];
  saveButtonText: string;
  view: boolean;
  countries: JsonApi<CountryOption[]>;
  leadSources: JsonApi<ReasonGet[]>;
}

const LeadForm = ({
  lead,
  fields,
  saveButtonText,
  view,
  countries,
  leadSources,
}: LeadFormProps) => {
  const [customerLead] = useState(() => new CustomerLead(deserialise(lead).data));
  const countryOptions = deserialise(countries).data as CountryOption[];
  const leadSourceOptions = deserialise(leadSources).data as ReasonGet[];
  const mainContactInfoId = customerLead.mainContactInfo?.id;
  const secondaryContactInfoId = customerLead.secondaryContactInfo?.id;
  const [createSecondary, setCreateSecondary] = useState(
    secondaryContactInfoId ? true : false
  );
  const leadSourceField = useField(fields, 'lead_source');

  const removeContactInfo = (id: number, type: string) => {
    const removeInfoText = () => {
      if (type === 'main') {
        return 'Main Contact Info';
      } else {
        return 'Secondary Contact Info';
      }
    };

    const removeText = `${I18n.translate('buttons.remove')} ${removeInfoText()}`;
    return (
      <FieldSetWithTitle title={''}>
        <a
          data-confirm='Are you sure?'
          rel='nofollow'
          data-method='delete'
          href={Routes.contact_info_path(id)}
          className='button button--small button--required'
        >
          <Icon icon='cross' />
          {removeText}
        </a>
      </FieldSetWithTitle>
    );
  };

  return (
    <>
      <MainContactInfo data={customerLead} type='main' fields={fields} />
      <FieldSetWithTitle title={''}>
        <SelectGroup
          name='customer_lead[lead_source_id]'
          label={I18n.translate('attributes.lead_source')}
          placeholder={I18n.translate('leads.form.select_lead_source')}
          options={leadSourceOptions}
          onChange={(e) => customerLead.setLeadSourceId(e.target.value)}
          value={customerLead.leadSourceId || ''}
          disabled={!leadSourceField?.enabled}
          required={true}
        />
      </FieldSetWithTitle>
      {mainContactInfoId &&
        secondaryContactInfoId &&
        removeContactInfo(mainContactInfoId, 'main')}

      <FieldSetWithTitle title={''}>
        <Checkbox
          label={I18n.translate('leads.form.secondary_contact_question')}
          name={'customer[address_equals_billing_address]'}
          icon='tick'
          checked={createSecondary}
          onChange={() => setCreateSecondary(!createSecondary)}
        />
      </FieldSetWithTitle>

      {createSecondary && (
        <>
          <MainContactInfo data={customerLead} type={'secondary'} fields={fields} />
          {secondaryContactInfoId &&
            removeContactInfo(secondaryContactInfoId, 'secondary')}
        </>
      )}

      <LeadDetails data={customerLead} fields={fields} countries={countryOptions} />

      <SubmitRow
        data={customerLead}
        view={view}
        cancelPath={'/'}
        saveButtonText={saveButtonText}
      />
    </>
  );
};

export default observer(LeadForm);
